import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { api, type ChatFile, type ChatImage, type StoredMessage } from '../api';
import { useT } from '../i18n';
import {
  type MicrosoftMailSendDraft,
  readCsrfTokenFromCookie,
  type StreamNoticeCode,
  streamChatPost,
} from '../lib/sse';

export type ToolEvent = { name: string; status: 'running' | 'done'; ok?: boolean };

export type StreamingState = {
  /** Live assistant text being assembled. */
  text: string;
  /** Active tool calls, latest last. */
  tools: ToolEvent[];
  /** Set when the stream surfaces a `chat_event:"error"` or fetch fails. */
  error: string | null;
  /** True from `send` until the stream closes (success or error). */
  busy: boolean;
  mailSendDraft: MicrosoftMailSendDraft | null;
  /** Recovery notes of the last answer; kept until the next message is sent. */
  notices: StreamNoticeCode[];
  /** Model "nivio": progress events of the running answer (lib/orchestration.ts). */
  orchestration: unknown[];
};

const initial: StreamingState = {
  text: '',
  tools: [],
  error: null,
  busy: false,
  mailSendDraft: null,
  notices: [],
  orchestration: [],
};

type Translate = ReturnType<typeof useT>;

/**
 * Streams live outside the React tree, one per conversation, so a conversation keeps
 * generating when the user opens another page or conversation. The page that shows it
 * simply reads the current state again when it is opened.
 */
type Entry = {
  state: StreamingState;
  abort: AbortController | null;
  runId: number;
  /** Text of the last answer that finished well, e.g. to read it aloud. */
  answer: string;
};
const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();

function entryFor(id: string): Entry {
  let entry = entries.get(id);
  if (!entry) {
    entry = { state: initial, abort: null, runId: 0, answer: '' };
    entries.set(id, entry);
  }
  return entry;
}

function publish(id: string, patch: (s: StreamingState) => StreamingState): void {
  const entry = entryFor(id);
  entry.state = patch(entry.state);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Ids of the conversations that are generating right now. */
let busyIds: string[] = [];
function busySnapshot(): string[] {
  const next = [...entries].filter(([, e]) => e.state.busy).map(([id]) => id);
  if (next.length !== busyIds.length || next.some((id, i) => id !== busyIds[i])) busyIds = next;
  return busyIds;
}

export function useBusyConversationIds(): string[] {
  return useSyncExternalStore(subscribe, busySnapshot, busySnapshot);
}

/** Stops every running stream, for sign-out. */
export function stopAllChatStreams(): void {
  for (const [id, entry] of entries) {
    entry.runId += 1;
    entry.abort?.abort();
    entry.abort = null;
    publish(id, () => initial);
  }
}

async function runStream(
  qc: QueryClient,
  t: Translate,
  conversationId: string,
  path: string,
  body: unknown,
  optimisticUser: string | null,
  images: ChatImage[],
): Promise<boolean> {
  // Optimistically append the user's message so the UI doesn't flicker
  // while the round-trip + DB write completes.
  if (optimisticUser !== null) {
    qc.setQueryData<{ messages: StoredMessage[] } | undefined>(
      ['conversation', conversationId],
      (prev) => {
        if (!prev) return prev;
        const optimistic: StoredMessage = {
          id: `optimistic-${Date.now()}`,
          role: 'user',
          content: { text: optimisticUser, ...(images.length ? { images } : {}) },
          tokensIn: null,
          tokensOut: null,
          finishReason: null,
          latencyMs: null,
          datasetGroupSlugs: [],
          createdAt: new Date().toISOString(),
        };
        return { ...prev, messages: [...prev.messages, optimistic] };
      },
    );
  }

  const entry = entryFor(conversationId);
  entry.abort?.abort();
  const ctl = new AbortController();
  const runId = entry.runId + 1;
  entry.runId = runId;
  entry.abort = ctl;
  publish(conversationId, () => ({
    text: '',
    tools: [],
    error: null,
    busy: true,
    mailSendDraft: null,
    notices: [],
    orchestration: [],
  }));
  const current = () => entryFor(conversationId).runId === runId;

  let succeeded = true;
  try {
    for await (const evt of streamChatPost({
      url: path,
      body,
      csrfToken: readCsrfTokenFromCookie(),
      signal: ctl.signal,
    })) {
      if (!current()) return false;
      if (evt.kind === 'content_delta') {
        publish(conversationId, (s) => ({ ...s, text: s.text + evt.text }));
      } else if (evt.kind === 'tool_call_start') {
        publish(conversationId, (s) => ({
          ...s,
          tools: [...s.tools, { name: evt.name, status: 'running' }],
        }));
      } else if (evt.kind === 'tool_call_done') {
        publish(conversationId, (s) => ({
          ...s,
          tools: s.tools.map((tool) =>
            tool.name === evt.name && tool.status === 'running'
              ? { ...tool, status: 'done', ok: evt.ok }
              : tool,
          ),
        }));
      } else if (evt.kind === 'mail_send_confirmation_required') {
        publish(conversationId, (s) => ({ ...s, mailSendDraft: evt.draft }));
      } else if (evt.kind === 'error') {
        succeeded = false;
        publish(conversationId, (s) => ({ ...s, error: evt.message }));
      } else if (evt.kind === 'orchestration') {
        publish(conversationId, (s) => ({ ...s, orchestration: [...s.orchestration, evt.event] }));
      } else if (evt.kind === 'replace_text') {
        publish(conversationId, (s) => ({ ...s, text: evt.text }));
      } else if (evt.kind === 'notice') {
        publish(conversationId, (s) =>
          s.notices.includes(evt.code) ? s : { ...s, notices: [...s.notices, evt.code] },
        );
      } else if (evt.kind === 'limit_reached') {
        succeeded = false;
        publish(conversationId, (s) => ({ ...s, error: t('toolIterationCapReached') }));
      }
      // `finish`: text and usage are stable; the canonical history is refetched below.
    }
  } catch (err) {
    succeeded = false;
    if (current() && (err as { name?: string }).name !== 'AbortError') {
      publish(conversationId, (s) => ({ ...s, error: String(err) }));
    }
  } finally {
    if (current()) {
      const finished = entryFor(conversationId);
      finished.abort = null;
      finished.answer = succeeded && !ctl.signal.aborted ? finished.state.text : '';
      // Refetch the canonical history *first*, then drop the streaming overlay.
      // The other order shows the streamed text next to the freshly persisted
      // assistant row, which looks like a duplicate answer.
      await qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      await qc.invalidateQueries({ queryKey: ['conversations'] });
      refetchWhileUntitled(qc, conversationId);
      if (current()) {
        publish(conversationId, (s) => ({
          text: '',
          tools: [],
          error: s.error,
          busy: false,
          mailSendDraft: s.mailSendDraft,
          notices: s.notices,
          // The stored answer carries the plan from here on.
          orchestration: [],
        }));
      }
    }
  }
  return succeeded && !ctl.signal.aborted && current();
}

/** The server writes the generated title a few seconds after the answer; pick it up when it lands. */
const TITLE_REFETCH_DELAYS_MS = [2_000, 6_000, 15_000];

function refetchWhileUntitled(qc: QueryClient, conversationId: string) {
  const untitled = () =>
    qc.getQueryData<{ title?: string }>(['conversation', conversationId])?.title ===
    'New conversation';
  if (!untitled()) return;
  for (const delay of TITLE_REFETCH_DELAYS_MS) {
    setTimeout(() => {
      if (!untitled()) return;
      void qc.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    }, delay);
  }
}

/**
 * The stream of one conversation. `send` and `regenerate` start it; it keeps running when
 * the caller unmounts (opening another page does not stop it). `stop` aborts it.
 */
export function useChatStream(conversationId: string) {
  const qc = useQueryClient();
  const t = useT();
  const state = useSyncExternalStore(
    subscribe,
    () => entryFor(conversationId).state,
    () => initial,
  );

  const run = useCallback(
    (path: string, body: unknown, optimisticUser: string | null, images: ChatImage[] = []) =>
      runStream(qc, t, conversationId, path, body, optimisticUser, images),
    [qc, t, conversationId],
  );

  const send = useCallback(
    (content: string, visibleContent = content, images: ChatImage[] = [], files: ChatFile[] = []) =>
      run(
        `/api/conversations/${conversationId}/messages`,
        {
          content,
          visibleContent,
          ...(images.length ? { images } : {}),
          ...(files.length ? { files } : {}),
        },
        visibleContent,
        images,
      ),
    [conversationId, run],
  );

  const regenerate = useCallback(
    (
      options: {
        nivioConfirm?: boolean;
        nivioRun?: { id: string; skip: string[]; models?: Record<string, string> };
      } = {},
    ) =>
      run(
        `/api/conversations/${conversationId}/regenerate`,
        {
          ...(options.nivioConfirm ? { nivioConfirm: true } : {}),
          ...(options.nivioRun ? { nivioRun: options.nivioRun } : {}),
        },
        null,
      ),
    [conversationId, run],
  );

  const stop = useCallback(() => {
    // Closing the connection no longer stops the answer on the server, so ask for it explicitly.
    void api.stopConversation(conversationId).catch(() => undefined);
    entryFor(conversationId).abort?.abort();
  }, [conversationId]);

  /** Clears an old result or error; a stream that is still running is left alone. */
  const reset = useCallback(() => {
    const entry = entryFor(conversationId);
    if (entry.state.busy) return;
    publish(conversationId, () => initial);
  }, [conversationId]);

  const clearMailSendDraft = useCallback(() => {
    publish(conversationId, (s) => ({ ...s, mailSendDraft: null }));
  }, [conversationId]);

  const lastAnswer = useCallback(() => entryFor(conversationId).answer, [conversationId]);

  return { ...state, send, regenerate, stop, reset, clearMailSendDraft, lastAnswer };
}
